import React from "react";
import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Plane, Building2, Home, Layers, ArrowUpRight, Globe2, Wallet, Lock } from "lucide-react";
import { useLang } from "../../i18n/LanguageProvider";
import { AGENCY, TRENDING, CURRENCIES, LANGUAGES_SUPPORTED } from "../../i18n/dict";
import { scrollToId } from "./SmoothScroll";

const MODULE_ICONS = { flights: Plane, hotels: Building2, apartments: Home, flight_and_hotel: Layers };

const lineReveal = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.05, delay: 0.15 + i * 0.1, ease: [0.76, 0, 0.24, 1] },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: 0.4 + i * 0.08, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const BookingEngine = () => {
  const { t } = useLang();
  const navigate = useNavigate();

  return (
    <section
      id="booking-engine"
      data-testid="engine-section"
      className="relative bg-[#EAE3D2] text-[#0B1416] overflow-hidden"
    >
      <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-28 md:py-40">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left column */}
          <div className="lg:col-span-6 relative">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.3 }}
              className="overline text-[#0D7A7D]"
            >
              — {t.engine.eyebrow}
            </motion.div>

            <h2
              data-testid="engine-title"
              className="font-heading-tight text-5xl md:text-6xl lg:text-7xl leading-[0.92] mt-6"
            >
              {t.engine.title_lines.map((line, i) => (
                <span key={i} className="mask-line">
                  <motion.span
                    variants={lineReveal}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.3 }}
                    custom={i}
                    className="inline-block"
                  >
                    {i === 1 ? (
                      <span className="font-italic text-[#0D7A7D]">{line}</span>
                    ) : (
                      line
                    )}
                  </motion.span>
                </span>
              ))}
            </h2>

            <motion.p
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.4 }}
              custom={1}
              className="mt-8 text-base md:text-lg font-light leading-relaxed opacity-75 max-w-lg"
            >
              {t.engine.body}
            </motion.p>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.4 }}
              custom={2}
              className="mt-10 flex flex-wrap gap-3"
            >
              <button
                data-testid="engine-cta-open"
                onClick={() => navigate("/book?tab=flights")}
                className="tt-btn tt-btn-dark"
              >
                <Lock size={12} />
                {t.engine.cta_primary}
                <ArrowUpRight size={14} />
              </button>
              <button
                data-testid="engine-cta-advisor"
                onClick={() => scrollToId("contact")}
                className="tt-btn text-[12px] tracking-[0.22em] uppercase font-medium border border-[#0B1416]/25 hover:bg-[#0B1416] hover:text-[#F5F1E8] transition-colors"
              >
                {t.engine.cta_secondary}
              </button>
            </motion.div>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              custom={3}
              className="mt-6 flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] opacity-50"
            >
              <Lock size={10} />
              {t.engine.note}
            </motion.div>
          </div>

          {/* Right column — modules card */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            custom={2}
            className="lg:col-span-6"
          >
            <div className="bg-[#0B1416] text-[#F5F1E8] p-8 md:p-12 relative overflow-hidden">
              {/* Ambient glow */}
              <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-[#1BB6B9]/18 blur-[100px] pointer-events-none" />

              <div className="absolute -top-3 left-8 bg-[#6EE3E6] text-[#0B1416] px-3 py-1 text-[10px] tracking-[0.28em] uppercase font-medium">
                Live
              </div>

              {/* Modules grid */}
              <div className="relative grid grid-cols-2 gap-4">
                {t.engine.modules.map((mod, i) => {
                  const Icon = MODULE_ICONS[mod.key] || Plane;
                  return (
                    <button
                      key={mod.key}
                      type="button"
                      onClick={() => navigate(`/book?tab=${mod.key}`)}
                      data-testid={`engine-module-${mod.key}`}
                      className="group relative text-left border border-[#F5F1E8]/15 p-6 hover:border-[#6EE3E6]/60 hover:bg-[#6EE3E6]/5 transition-all"
                    >
                      <Icon
                        size={26}
                        strokeWidth={1.3}
                        className="text-[#6EE3E6] group-hover:scale-110 transition-transform"
                      />
                      <div className="mt-6 font-heading text-2xl md:text-3xl leading-none">
                        {mod.label}
                      </div>
                      <div className="mt-3 flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] opacity-60 group-hover:opacity-100 transition-opacity">
                        Réserver
                        <ArrowUpRight size={12} />
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Currencies + Languages */}
              <div className="relative grid grid-cols-2 gap-8 mt-10 pt-8 border-t border-[#F5F1E8]/12">
                <div>
                  <div className="overline text-[#6EE3E6] mb-4 flex items-center gap-2">
                    <Wallet size={12} />
                    {t.engine.currencies_label}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {CURRENCIES.map((c) => (
                      <span
                        key={c}
                        className="text-[10px] tracking-[0.18em] uppercase font-medium px-2.5 py-1 border border-[#F5F1E8]/18 rounded-full"
                      >
                        {c}
                      </span>
                    ))}
                    <span className="text-[10px] tracking-[0.18em] uppercase px-2.5 py-1 opacity-50">
                      +30
                    </span>
                  </div>
                </div>
                <div>
                  <div className="overline text-[#6EE3E6] mb-4 flex items-center gap-2">
                    <Globe2 size={12} />
                    {t.engine.languages_label}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {LANGUAGES_SUPPORTED.map((l) => (
                      <span
                        key={l}
                        className="text-[10px] tracking-[0.18em] uppercase font-medium px-2.5 py-1 border border-[#F5F1E8]/18 rounded-full"
                      >
                        {l}
                      </span>
                    ))}
                    <span className="text-[10px] tracking-[0.18em] uppercase px-2.5 py-1 opacity-50">
                      +10
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Trending destinations marquee */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          custom={4}
          className="mt-24"
        >
          <div className="flex items-baseline justify-between mb-8">
            <div className="overline text-[#0D7A7D]">— {t.engine.trending_label}</div>
            <span className="hidden md:inline text-[11px] uppercase tracking-[0.28em] opacity-50">
              6 villes · live
            </span>
          </div>

          <Marquee gradient={false} speed={30} pauseOnHover>
            {TRENDING.concat(TRENDING).map((d, i) => (
              <button
                key={`${d.name}-${i}`}
                type="button"
                onClick={() => navigate(`/book?tab=hotels&dest=${encodeURIComponent(d.name)}`)}
                data-testid={`trending-${d.name.toLowerCase()}-${i}`}
                className="group flex items-center gap-5 mr-6 md:mr-10"
              >
                <div className="clip-frame w-16 h-16 md:w-20 md:h-20 shrink-0">
                  <img src={d.img} alt={d.name} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="pr-4 text-left">
                  <div className="font-heading text-2xl md:text-3xl leading-none group-hover:text-[#0D7A7D] transition-colors">
                    {d.name}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.28em] mt-1.5 opacity-55">
                    {d.country}
                  </div>
                </div>
                <span className="text-[#1BB6B9]" style={{ fontSize: 8 }}>●</span>
              </button>
            ))}
          </Marquee>
        </motion.div>
      </div>
    </section>
  );
};
